import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Star, ArrowRight, Car, Ship, FileText, MessageCircle } from "lucide-react";
import { heroImages, services, companyInfo } from "../data/mockData";
import { useVehicles } from "../hooks/useVehicles";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { vehicles, loading, error, refetch } = useVehicles({ limit: 6 });

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${companyInfo.whatsapp}`, "_blank");
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Image Slider */}
      <section className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={image.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 hero-gradient"></div>
            </div>
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 flex items-center justify-center h-full">
          <div className="container mx-auto text-center">
            <div className="animate-fadeInUp">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                {heroImages[currentSlide].title}
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
                {heroImages[currentSlide].subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/inventory" className="btn-primary text-lg px-8 py-4">
                  <Car className="w-5 h-5" />
                  View Inventory
                </Link>
                <button onClick={handleWhatsAppClick} className="btn-secondary text-lg px-8 py-4">
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Chat
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Slider Controls */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? "bg-royal-red" : "bg-white/50"
              }`}
            />
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-6 top-1/2 transform -translate-y-1/2 glass-effect p-3 rounded-full hover:bg-white/20 transition-all"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-6 top-1/2 transform -translate-y-1/2 glass-effect p-3 rounded-full hover:bg-white/20 transition-all"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </section>

      {/* Company Strengths */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center glass-effect p-8 rounded-xl hover-lift">
              <div className="bg-royal-red p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Trusted Global Partner</h3>
              <p className="text-gray-300">Over a decade of experience in premium automotive trading worldwide.</p>
            </div>
            <div className="text-center glass-effect p-8 rounded-xl hover-lift">
              <div className="bg-royal-red p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Ship className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Fast Delivery</h3>
              <p className="text-gray-300">Efficient logistics and shipping solutions for timely vehicle delivery.</p>
            </div>
            <div className="text-center glass-effect p-8 rounded-xl hover-lift">
              <div className="bg-royal-red p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Authentic Imports</h3>
              <p className="text-gray-300">Verified authentic vehicles with complete documentation and certification.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Inventory */}
      <section className="section-padding bg-black">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Featured Inventory</h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Discover our premium collection of luxury vehicles, carefully selected and verified for exceptional quality.
            </p>
          </div>
          
          {loading && (
            <div className="flex justify-center">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {error && (
            <ErrorMessage 
              message={error} 
              onRetry={refetch}
              className="max-w-md mx-auto"
            />
          )}

          {!loading && !error && vehicles.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {vehicles.slice(0, 6).map((vehicle) => (
                <div key={vehicle.id} className="glass-effect rounded-xl overflow-hidden hover-lift group">
                  <div className="relative overflow-hidden">
                    <img
                      src={vehicle.image}
                      alt={`${vehicle.make} ${vehicle.model}`}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        vehicle.status === 'Available' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-black'
                      }`}>
                        {vehicle.status}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-2">
                      {vehicle.make} {vehicle.model}
                    </h3>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-300 mb-4">
                      <span>Year: {vehicle.year}</span>
                      <span>Mileage: {vehicle.mileage}</span>
                      <span>Fuel: {vehicle.fuel}</span>
                      <span>Color: {vehicle.color}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-bold text-royal-red">{vehicle.price}</span>
                      <button 
                        onClick={handleWhatsAppClick}
                        className="btn-primary text-sm"
                      >
                        Inquire Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {!loading && !error && (
            <div className="text-center mt-12">
              <Link to="/inventory" className="btn-primary text-lg px-8 py-4">
                View All Inventory
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Our Services</h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Comprehensive automotive solutions from auction bidding to global shipping and everything in between.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service) => (
              <div key={service.id} className="glass-effect p-6 rounded-xl hover-lift">
                <div className="bg-royal-red p-3 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-gray-300 mb-4">{service.description}</p>
                <ul className="text-sm text-gray-400 space-y-1">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-royal-red rounded-full mr-2"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services" className="btn-primary text-lg px-8 py-4">
              View All Services
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-royal-red">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Find Your Perfect Vehicle?</h2>  
          <p className="text-xl text-gray-100 mb-8 max-w-2xl mx-auto">
            Contact our experts today for personalized assistance in finding and importing your dream car from Japan.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-royal-red hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center">
              Get Free Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <button 
              onClick={handleWhatsAppClick}
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-royal-red px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp Chat
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;